import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, ChevronDown } from "lucide-react";

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Calendar } from "../../ui/calendar";
import { Checkbox } from "../../ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PrcDialogContent } from "./PrcDialogContent";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import { cn } from "../../utils/common/cn";
import {
  thr,
  usdM,
} from "./format";
import { useFacilities } from "../../utils/portfolio-risk-concentration/api/facilities";
import {
  useJointCandidates,
  useDismissJointCandidate,
  type JointCandidate,
  type JointDef,
} from "../../utils/portfolio-risk-concentration/api/joints";
import type {
  FacilityType,
  LimitConfigRow,
  ProposeInput,
} from "../../utils/portfolio-risk-concentration/api/limit-config";

/**
 * Define / edit / archive a joint.
 *
 * Was the body of the Manage limits -> Joints tab. That tab is gone: a joint is not a
 * separate kind of thing to manage, so its definitions now sit in Approved View beside
 * the single limits, and this dialog opens from "+ Define joint" next to "+ Add limit".
 *
 * Two buttons rather than one, on purpose. "Add limit" collects a RULE — dimension,
 * operator, match values, measure, a threshold and optionally a dollar cap. A joint has no
 * rule of its own: it is a NAME over two or more limits that already exist, and it is
 * GOVERNED BY ITS TIGHTEST MEMBER — the one with the lowest threshold — which supplies its
 * threshold, percentage, status and headroom. So there is nothing to set here beyond the
 * members and an alert distance. The only thing the two forms share is the
 * propose -> review envelope, and they diverge again immediately after it
 * (_apply_joint_add vs _apply_internal / _apply_external_add). Folding them into one
 * form would mean most fields appearing and disappearing behind a type switch.
 *
 * Everything here submits through the EXISTING propose -> review flow rather than a
 * dedicated write endpoint — that is what makes joints governed like every other limit
 * change, and there is deliberately no POST /joints to shortcut it.
 */

/** The alert distance is a free-text percentage; these are the bounds it is held to. */
const ALERT_PCT_MIN = 0;
const ALERT_PCT_MAX = 100;

// How a facility reads (CAP1 -> "CAP 1 Back Book") comes from the facilities the database lists (see `facilityLabel` in the component);
// the internal side is the one book, INTERNAL.
const INTERNAL_LABEL = "Internal risk appetite";

interface JointFormState {
  jointId: string | null;
  jointName: string;
  memberRefs: string[];
  /** Held as the raw field text so the input can be cleared mid-edit; parsed on submit. */
  alertPct: string;
  effectiveStartDate: Date;
  reason: string;
}

const emptyJointForm = (): JointFormState => ({
  jointId: null,
  jointName: "",
  memberRefs: [],
  alertPct: "10",
  effectiveStartDate: new Date(),
  reason: "",
});

export interface DefineJointDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which side of Approved View is showing — sets the default book. */
  facilityType: FacilityType;
  externalLimits: LimitConfigRow[];
  internalLimits: LimitConfigRow[];
  propose: (input: ProposeInput) => void | Promise<unknown>;
  /** The joint being edited, or null/undefined to define a new one. */
  editJoint?: JointDef | null;
  /** Called with a human-readable confirmation once a proposal is submitted. */
  onSubmitted?: (message: string) => void;
}

export function DefineJointDialog({
  open,
  onOpenChange,
  facilityType,
  externalLimits,
  internalLimits,
  propose,
  editJoint,
  onSubmitted,
}: DefineJointDialogProps) {
  // The books a joint can live in, per side of Manage limits: the facilities that have limits for this investor (the external limits on
  // screen carry their facility code), or the one internal book. Read from the data on screen, not a list kept here.
  const books = useMemo(
    () => (facilityType === "internal" ? ["INTERNAL"] : [...new Set(externalLimits.map((l) => l.facilityCode))]),
    [facilityType, externalLimits],
  );
  const facilitiesQ = useFacilities();
  const FACILITY_LABEL = useMemo(() => {
    const out: Record<string, string> = { INTERNAL: INTERNAL_LABEL };
    for (const f of facilitiesQ.data ?? []) out[f.code] = f.name;
    return out;
  }, [facilitiesQ.data]);
  const [book, setBook] = useState<string>(books[0] ?? "");
  // the chosen book follows the data: when the investor (and so the list) changes, it is one of the new list's
  useEffect(() => {
    setBook((current) => (books.includes(current) ? current : (books[0] ?? "")));
  }, [books]);
  const [form, setForm] = useState<JointFormState>(emptyJointForm);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Every approved limit, across facilities — the multi-select shows all of them and
  // DISABLES the ones a pick has ruled out (wrong facility, or a category already taken)
  // rather than hiding them, so the rules are taught rather than enforced invisibly.
  const allLimits = useMemo(
    () => [...externalLimits, ...internalLimits],
    [externalLimits, internalLimits],
  );

  // lockedFacility is DERIVED from the first pick, never stored: there is no second copy
  // of the truth to desync.
  const lockedFacility = form.memberRefs.length
    ? allLimits.find((l) => l.limitId === form.memberRefs[0])?.facilityCode ?? null
    : null;

  const selectedLimits = form.memberRefs
    .map((ref) => allLimits.find((l) => l.limitId === ref))
    .filter(Boolean) as LimitConfigRow[];

  // A joint crosses DIMENSIONS — obligor x tenure, industry x equipment. Two limits from
  // the same category are the same question asked twice: the tighter one would govern and
  // the other would never bind, so once a category is picked its siblings are disabled.
  const takenCategories = new Set(selectedLimits.map((l) => l.categoryGroup));

  // Candidates are ranked per facility, so they follow the first pick once there is
  // one and the book selector before that.
  const candidateFacility = lockedFacility ?? book;
  const { data: candidatesData, isLoading: candidatesLoading } =
    useJointCandidates(candidateFacility);
  const dismiss = useDismissJointCandidate();
  const candidates = candidatesData?.candidates ?? [];
  const excluded = candidatesData?.excludedLimits ?? [];

  // Reset on every open so a cancelled edit never leaks into the next new joint.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setBook(books[0] ?? "");
    if (editJoint) {
      setForm({
        jointId: editJoint.jointId,
        jointName: editJoint.jointName,
        memberRefs: editJoint.members.map((m) => m.memberRef),
        alertPct: String(editJoint.alertDistToThresholdPct ?? 10),
        effectiveStartDate: editJoint.effectiveStartDate
          ? new Date(`${editJoint.effectiveStartDate}T00:00:00`)
          : new Date(),
        reason: "",
      });
      // An edit already has its members; suggestions would only be noise.
      setShowSuggestions(false);
    } else {
      setForm(emptyJointForm());
      setShowSuggestions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editJoint?.jointId, facilityType]);

  function confirmCandidate(c: JointCandidate) {
    // Deliberately does NOT create the joint — it fills this form in with the
    // suggestion's own words seeded as the reason, and the governed proposal stays
    // the only way in.
    setForm((f) => ({
      ...f,
      jointName: c.suggestedName,
      memberRefs: [...c.memberRefs],
      reason: c.overlapSentence,
    }));
    setShowSuggestions(false);
  }

  function toggleMember(ref: string, checked: boolean) {
    setForm((f) => {
      const set = new Set(f.memberRefs);
      if (checked) set.add(ref);
      else set.delete(ref);
      return { ...f, memberRefs: [...set] };
    });
  }

  async function submit(changeType: ProposeInput["changeType"]) {
    setError(null);
    if (changeType === "ADD_JOINT" || changeType === "MODIFY_JOINT") {
      if (form.memberRefs.length < 2) {
        setError("A joint needs at least two limits.");
        return;
      }
      const facilities = new Set(selectedLimits.map((l) => l.facilityCode));
      if (facilities.size > 1) {
        // A joint's members must share one denominator; min() across two different
        // facility totals would not mean anything.
        setError("Every limit in a joint must belong to the same facility.");
        return;
      }
      if (!form.jointName.trim()) {
        setError("A joint name is required.");
        return;
      }
    }
    if (!form.reason.trim()) {
      setError("A reason is required for every proposal.");
      return;
    }
    // Free text, so it can be empty or nonsense — checked here rather than blocked at the
    // keystroke, which would fight anyone typing "12.5" one character at a time.
    const alertPct = Number(form.alertPct.trim());
    if (
      form.alertPct.trim() === "" ||
      !Number.isFinite(alertPct) ||
      alertPct <= ALERT_PCT_MIN ||
      alertPct > ALERT_PCT_MAX
    ) {
      setError(`Alert distance must be a number above ${ALERT_PCT_MIN} and at most ${ALERT_PCT_MAX}.`);
      return;
    }

    const facility = lockedFacility ?? book;
    const input: ProposeInput = {
      limitId: null,
      facilityType: facility === "INTERNAL" ? "internal" : "external",
      facilityCode: facility,
      categoryGroup: "Joint",
      limitDesc: form.jointName.trim(),
      // A joint has no threshold of its own; this column carries its alert
      // distance-to-threshold. See ProposeInput's docstring.
      newThresholdPct: alertPct,
      newCapitalCapDollars: null,
      effectiveStartDate: format(form.effectiveStartDate, "yyyy-MM-dd"),
      reason: form.reason.trim(),
      changeType,
      jointId: form.jointId,
      jointName: form.jointName.trim(),
      memberRefs: form.memberRefs,
      memberLimitDescs: selectedLimits.map((l) => l.limitDesc),
      alertDistToThresholdPct: alertPct,
    };

    try {
      setSubmitting(true);
      await propose(input);
      onOpenChange(false);
      onSubmitted?.(
        changeType === "DELETE_JOINT"
          ? `Archival of "${form.jointName}" proposed.`
          : `Joint "${form.jointName}" proposed.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not submit the proposal.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{form.jointId ? "Edit joint" : "Define a joint"}</DialogTitle>
          <DialogDescription>
            Pick two or more limits within one facility, at most one per category. Nothing
            else is needed — the joint
            is judged on whichever member carries the LOWEST threshold, and takes that limit's
            threshold, percentage, status and headroom from its own already-tracked numbers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* ── Suggested combinations ──────────────────────────────────────────
              Lives inside the dialog because that is all it ever did: confirming a
              suggestion creates nothing, it fills this form in. As its own card on a
              page it read as a second, ungoverned way to make a joint. */}
          {!form.jointId && (
            <div className="rounded-md border border-border/60">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-muted/30"
                onClick={() => setShowSuggestions((s) => !s)}
              >
                <span>
                  <span className="font-medium">Suggested combinations</span>
                  {candidates.length > 0 && (
                    <span className="ml-1.5 text-muted-foreground">({candidates.length})</span>
                  )}
                  <span className="ml-2 text-muted-foreground">
                    ranked by how often these categories are genuinely the same loans
                  </span>
                </span>
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                    showSuggestions && "rotate-180",
                  )}
                />
              </button>

              {showSuggestions && (
                <div className="space-y-3 border-t border-border/60 p-3">
                  <p className="text-xs text-muted-foreground">
                    Most of the mathematically possible combinations never co-occur in the
                    current portfolio, so they are not worth watching. Confirming one only
                    fills in the form below — it does not create anything.
                  </p>

                  {candidatesLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
                  {!candidatesLoading && !candidates.length && (
                    <p className="text-sm text-muted-foreground">
                      No suggestions for {FACILITY_LABEL[candidateFacility] ?? candidateFacility}.
                      You can still pick members manually below.
                    </p>
                  )}

                  {candidates.map((c) => (
                    <div
                      key={c.candidateId}
                      className={cn(
                        "rounded-lg border border-border p-3",
                        c.isNested && "border-border/50 bg-muted/10",
                      )}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {c.limitDescs.map((d) => (
                              <Badge key={d} variant="secondary" className="text-[11px]">
                                {d}
                              </Badge>
                            ))}
                            {c.isNested && (
                              <Badge
                                variant="outline"
                                className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground"
                              >
                                nested
                              </Badge>
                            )}
                          </div>
                          <p className="mt-2 text-sm">{c.overlapSentence}</p>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            Containment {(c.containment * 100).toFixed(0)}% · Jaccard{" "}
                            {(c.jaccard * 100).toFixed(0)}%
                            {c.projectedJointHeadroomDollars !== null && (
                              <>
                                {" · governing member's headroom would be "}
                                <span className="tabular-nums">
                                  {usdM(c.projectedJointHeadroomDollars)}
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                        <div className="flex shrink-0 gap-2">
                          <Button size="sm" variant="outline" onClick={() => confirmCandidate(c)}>
                            Use
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => dismiss.mutate(c.candidateId)}
                          >
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {excluded.length > 0 && (
                    <details className="text-xs text-muted-foreground">
                      <summary className="cursor-pointer">
                        {excluded.length} limit(s) excluded from ranking
                      </summary>
                      <ul className="mt-2 space-y-1">
                        {excluded.map((e) => (
                          <li key={e.memberRef}>
                            · <span className="text-foreground">{e.limitDesc ?? e.memberRef}</span> —{" "}
                            {e.reason}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Members ─────────────────────────────────────────────────────────── */}
          <div className="rounded-md border border-border/60 bg-muted/20 p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs">
                Facility:{" "}
                <span className="font-medium">
                  {lockedFacility
                    ? FACILITY_LABEL[lockedFacility] ?? lockedFacility
                    : FACILITY_LABEL[book] ?? book}
                </span>
                {lockedFacility && (
                  <span className="text-muted-foreground"> — locked by your first pick</span>
                )}
              </p>
              <div className="flex items-center gap-3">
                {/* Only offered before the first pick and only when there is a choice
                    to make: the internal side has exactly one book. */}
                {!lockedFacility && books.length > 1 && (
                  <Select value={book} onValueChange={setBook}>
                    <SelectTrigger className="h-7 w-[170px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {books.map((b) => (
                        <SelectItem key={b} value={b}>
                          {FACILITY_LABEL[b] ?? b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <span className="whitespace-nowrap text-[11px] text-muted-foreground">
                  {form.memberRefs.length} of {allLimits.length} selected
                </span>
                {form.memberRefs.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => setForm((f) => ({ ...f, memberRefs: [] }))}
                  >
                    Change facility
                  </Button>
                )}
              </div>
            </div>

            <div className="mt-3 max-h-56 space-y-1 overflow-y-auto">
              {allLimits.map((l) => {
                const wrongFacility = !!lockedFacility && l.facilityCode !== lockedFacility;
                const checked = form.memberRefs.includes(l.limitId);
                // The row that IS the pick stays live so the pick can be undone.
                const sameCategory = !checked && takenCategories.has(l.categoryGroup);
                const blocked = wrongFacility || sameCategory;
                return (
                  <label
                    key={l.limitId}
                    className={cn(
                      "flex items-start gap-2 rounded px-1 py-1 text-xs",
                      blocked
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer hover:bg-muted/40",
                    )}
                  >
                    <Checkbox
                      className="mt-0.5"
                      checked={checked}
                      disabled={blocked}
                      onCheckedChange={(c) => toggleMember(l.limitId, !!c)}
                    />
                    <span className="min-w-0">
                      <span className="font-medium">{l.limitDesc}</span>
                      <span className="ml-1.5 text-muted-foreground">
                        {l.categoryGroup} · {thr(l.thresholdPct)} · {l.facilityCode}
                      </span>
                      {wrongFacility ? (
                        <span className="ml-1.5 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                          different facility
                        </span>
                      ) : sameCategory ? (
                        <span className="ml-1.5 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                          {l.categoryGroup} already picked
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="joint-name">Joint name</Label>
            <Input
              id="joint-name"
              value={form.jointName}
              placeholder="e.g. California + Manufacturing"
              onChange={(e) => setForm((f) => ({ ...f, jointName: e.target.value }))}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="joint-alert-pct">Alert when within</Label>
              <div className="relative">
                <Input
                  id="joint-alert-pct"
                  type="number"
                  inputMode="decimal"
                  min={ALERT_PCT_MIN}
                  max={ALERT_PCT_MAX}
                  step="0.5"
                  className="pr-7"
                  value={form.alertPct}
                  onChange={(e) => setForm((f) => ({ ...f, alertPct: e.target.value }))}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  %
                </span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Percent of the binding threshold.
              </p>
            </div>
            <div>
              <Label>Effective from</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start gap-2 font-normal">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {format(form.effectiveStartDate, "d MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={form.effectiveStartDate}
                    onSelect={(d) => d && setForm((f) => ({ ...f, effectiveStartDate: d }))}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div>
            <Label htmlFor="joint-reason">Reason</Label>
            <Textarea
              id="joint-reason"
              rows={3}
              placeholder="Why is this combination worth monitoring?"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {form.jointId && (
            <Button
              variant="destructive"
              disabled={submitting}
              onClick={() => submit("DELETE_JOINT")}
            >
              Propose archival
            </Button>
          )}
          <Button
            disabled={submitting}
            onClick={() => submit(form.jointId ? "MODIFY_JOINT" : "ADD_JOINT")}
          >
            {submitting ? "Submitting…" : form.jointId ? "Propose change" : "Propose joint"}
          </Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}
